// Simple analytics utility for tracking user events
// MVP: Console logging (can be upgraded to real analytics service later)

export type AnalyticsEvent =
  | 'page_view'
  | 'button_click'
  | 'order_initiated'
  | 'order_completed'
  | 'error_occurred';

interface EventData {
  [key: string]: string | number | boolean | undefined;
}

class Analytics {
  private enabled: boolean;

  constructor() {
    // Enable analytics in production, disable in development (unless explicitly enabled)
    this.enabled = import.meta.env.PROD || import.meta.env.VITE_ANALYTICS_ENABLED === 'true';
  }

  track(event: AnalyticsEvent, data?: EventData): void {
    if (!this.enabled) {
      return;
    }

    try {
      const eventPayload = {
        event,
        timestamp: new Date().toISOString(),
        data: data || {},
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // MVP: Console logging (upgrade to real service later)
      console.log('[Analytics]', eventPayload);

      // TODO: Send to real analytics service
      // Example: fetch('/api/analytics', { method: 'POST', body: JSON.stringify(eventPayload) });
    } catch (error) {
      // Analytics should never break the app
      console.error('[Analytics] Error tracking event:', error);
    }
  }

  pageView(path?: string): void {
    this.track('page_view', { path: path || window.location.pathname });
  }

  buttonClick(buttonName: string): void {
    this.track('button_click', { button: buttonName });
  }

  orderInitiated(): void {
    this.track('order_initiated');
  }

  orderCompleted(orderId?: string): void {
    this.track('order_completed', { orderId });
  }

  error(errorMessage: string, errorStack?: string): void {
    this.track('error_occurred', {
      message: errorMessage,
      stack: errorStack,
    });
  }
}

export const analytics = new Analytics();
